/**
 * Avatar utility functions for WINGO Wrapped
 */
import { useEffect, useState } from 'react';

/** Fallback when a member has no custom avatar file. */
export const DEFAULT_AVATAR_URL = '/avatars/default.png';

const USERNAME_ALIASES: Record<string, string> = {
  // Keep legacy typo compatibility.
  rachoon: 'rachoon',
};

const AVATAR_FILE_OVERRIDES: Record<string, string> = {
  // Existing file uses uppercase "L".
  l_o: 'L_o.png',
  // Existing asset format is jpeg.
  fig: 'fig.jpeg',
};

type AvatarOverrideListener = () => void;

const overrideListeners = new Set<AvatarOverrideListener>();
let avatarOverrides: Record<string, string> = {};
let avatarMapFailed = false;

function notifyAvatarOverrides() {
  overrideListeners.forEach((listener) => listener());
}

export function subscribeAvatarOverrides(listener: AvatarOverrideListener): () => void {
  overrideListeners.add(listener);
  return () => {
    overrideListeners.delete(listener);
  };
}

export function avatarApiUrlForUsername(username: string): string {
  return `/api/avatar?username=${encodeURIComponent(username.trim())}`;
}

function needsAvatarApiLookup(username: string): boolean {
  const trimmed = username.trim();
  if (!trimmed) return false;
  const spaced = trimmed.toLowerCase().replace(/\s+/g, '_');
  return normalizeAvatarKey(trimmed) !== spaced;
}

export async function loadAvatarOverrides(): Promise<void> {
  try {
    const res = await fetch('/api/avatar-map', { cache: 'no-store' });
    if (!res.ok) {
      avatarMapFailed = true;
      notifyAvatarOverrides();
      return;
    }
    const data = (await res.json()) as { avatars?: Record<string, string> };
    avatarOverrides = data.avatars ?? {};
    avatarMapFailed = false;
    notifyAvatarOverrides();
  } catch {
    avatarMapFailed = true;
    notifyAvatarOverrides();
  }
}

function normalizeAvatarKey(username: string): string {
  let normalized = username.trim().toLowerCase();
  normalized = normalized.replace(/\s+/g, '_');
  normalized = normalized.replace(/[àáâãäå]/g, 'a');
  normalized = normalized.replace(/[èéêë]/g, 'e');
  normalized = normalized.replace(/[ìíîï]/g, 'i');
  normalized = normalized.replace(/[òóôõö]/g, 'o');
  normalized = normalized.replace(/[ùúûü]/g, 'u');
  normalized = normalized.replace(/[ñ]/g, 'n');
  normalized = normalized.replace(/[^a-z0-9_\-]/g, '');
  return USERNAME_ALIASES[normalized] || normalized;
}

export function getConventionAvatarForUser(username: string): string {
  const canonical = normalizeAvatarKey(username);
  if (!canonical) return DEFAULT_AVATAR_URL;
  const filename = AVATAR_FILE_OVERRIDES[canonical] || `${canonical}.png`;
  return `/avatars/${filename}`;
}

export function getAvatarForUser(username: string): string {
  const keys = [
    username,
    username.trim(),
    username.trim().toLowerCase(),
    normalizeAvatarKey(username),
  ];
  for (const key of keys) {
    const override = avatarOverrides[key];
    if (override) return override;
  }
  if (avatarMapFailed || needsAvatarApiLookup(username)) {
    return avatarApiUrlForUsername(username);
  }
  return getConventionAvatarForUser(username);
}

export function useAvatarUrl(username: string): string {
  const [url, setUrl] = useState(() => getAvatarForUser(username));

  useEffect(() => {
    setUrl(getAvatarForUser(username));
    return subscribeAvatarOverrides(() => {
      setUrl(getAvatarForUser(username));
    });
  }, [username]);

  return url;
}
